import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, Card, Typography, Space, Tabs, Tag, Badge,
  Progress, message, Button,
} from "antd";
import { CheckSquareOutlined, WarningOutlined } from "@ant-design/icons";
import { tareasApi } from "../api/client";
import type { Tarea } from "../types";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const PRIORIDAD_COLORS: Record<string, string> = {
  Crítica: "red", Alta: "orange", Media: "blue", Baja: "default",
};

const ESTADO_COLORS: Record<string, string> = {
  Pendiente: "default", "En curso": "processing", "En revisión": "warning",
  Bloqueada: "error", Completada: "success", Cancelada: "default",
};

function TareasTable({ tareas, loading }: { tareas: Tarea[]; loading: boolean }) {
  const navigate = useNavigate();
  const columns = [
    { title: "ID", dataIndex: "task_id", width: 100, render: (v: string) => <Text code style={{ fontSize: 12 }}>{v}</Text> },
    { title: "Título", dataIndex: "titulo", ellipsis: true },
    { title: "Tipo", dataIndex: "tipo", width: 120, render: (v: string) => <Tag>{v}</Tag> },
    {
      title: "Prioridad", dataIndex: "prioridad", width: 90,
      render: (v: string) => <Tag color={PRIORIDAD_COLORS[v] ?? "default"}>{v}</Tag>,
    },
    {
      title: "Estado", dataIndex: "estado", width: 110,
      render: (v: string) => <Badge status={ESTADO_COLORS[v] as any} text={v} />,
    },
    {
      title: "Avance", dataIndex: "porcentaje_completado", width: 100,
      render: (v: number) => <Progress percent={v} size="small" showInfo={false} />,
    },
    {
      title: "Compromiso", dataIndex: "fecha_compromiso", width: 110,
      render: (v: string, r: Tarea) => {
        if (!v) return <Text type="secondary">—</Text>;
        const vencida = r.esta_vencida;
        return (
          <Space size={4}>
            {vencida && <WarningOutlined style={{ color: "#ff4d4f" }} />}
            <Text style={{ fontSize: 12, color: vencida ? "#ff4d4f" : undefined }}>
              {dayjs(v).format("DD/MM/YYYY")}
            </Text>
          </Space>
        );
      },
    },
    {
      title: "Proyecto", dataIndex: "proyecto_id", width: 90,
      render: (v: string) => v ? (
        <Button type="link" size="small" onClick={() => navigate(`/proyectos/${v}`)}>Ver</Button>
      ) : null,
    },
  ];

  return (
    <Table dataSource={tareas} columns={columns} rowKey="id"
      loading={loading} size="small"
      pagination={{ pageSize: 20, showTotal: (t) => `${t} tareas` }} />
  );
}

export default function TareasPage() {
  const [misTareas, setMisTareas] = useState<Tarea[]>([]);
  const [vencidas, setVencidas] = useState<Tarea[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      tareasApi.misTareas().then((r) => setMisTareas(r.data)),
      tareasApi.vencidas().then((r) => setVencidas(r.data)),
    ]).catch(() => message.error("Error cargando tareas"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0, color: "#1a1f5e" }}>Tareas</Title>
        <Text type="secondary" style={{ fontSize: 13 }}>Mis tareas activas y vencidas</Text>
      </div>
      <Card size="small">
        <Tabs
          items={[
            {
              key: "mis",
              label: (
                <Space>
                  <CheckSquareOutlined />
                  Mis tareas
                  {misTareas.length > 0 && <Tag>{misTareas.length}</Tag>}
                </Space>
              ),
              children: <TareasTable tareas={misTareas} loading={loading} />,
            },
            {
              key: "vencidas",
              label: (
                <Space>
                  <WarningOutlined style={{ color: vencidas.length > 0 ? "#ff4d4f" : undefined }} />
                  Vencidas
                  {vencidas.length > 0 && <Tag color="red">{vencidas.length}</Tag>}
                </Space>
              ),
              children: <TareasTable tareas={vencidas} loading={loading} />,
            },
          ]}
        />
      </Card>
    </div>
  );
}
